import { PERMISSIONS } from "@/services/permission.js"
import { FEATURE_SETS } from "@/services/featureSet.js"

// Zeiterfassung is a paid add-on, so every route here carries `requiresFeatureSet`: the permissions
// say which *user* may open the page, the feature-set says whether the *tenant* bought it at all.
// `featureSetGuard` sends anyone else to the add-on upsell, and the backend enforces the same
// feature-set on `/time-tracking/**` and `/work-time-model/**` (403 FEATURE_NOT_ENTITLED).
//
// "Meine Zeiten" carries RECORD_OWN_TIME only, not VIEW_TIME_ENTRY_FOREIGN: the page shows and edits
// the caller's own days — the backend derives the subject from the token, never from the request.
// Every role holds RECORD_OWN_TIME by default, so this bars nobody unless an admin takes it away.
export default [
  {
    path: "/timetracking",
    name: "TimeTrackingMy",
    component: () => import("@/views/timetracking/TimeTrackingMy.vue"),
    meta: {
      title: "timetracking.my.title",
      requiresAuth: true,
      requiresAllPermissions: [PERMISSIONS.RECORD_OWN_TIME],
      requiresFeatureSet: FEATURE_SETS.TIME_TRACKING,
    },
  },
  // One page, no tabs: the matrix, with the Anwesenheit banner above it. `#attendance` unfolds the
  // banner — the deep link the stale-clock notification uses — and `#violations` opens the matrix in
  // its Verstöße reading.
  {
    path: "/timetracking/review",
    name: "TimeTrackingReview",
    component: () => import("@/views/timetracking/TimeTrackingReview.vue"),
    meta: {
      title: "timetracking.review.title",
      requiresAuth: true,
      requiresAllPermissions: [PERMISSIONS.VIEW_TIME_ENTRY_FOREIGN],
      requiresFeatureSet: FEATURE_SETS.TIME_TRACKING,
    },
  },
  // The two screens the feature used to have. Both were reachable from a tab strip for months, so
  // bookmarks, open tabs and pasted links exist, and the catch-all would answer every one of them
  // with the 404 page. A redirect is cheaper than that page is helpful.
  //
  // Mitarbeiter's three views are tabs of the review screen, and its tab is in the hash — so the hash
  // is carried through and `/timetracking/team#attendance` still lands on the Anwesenheitstafel,
  // which is the one deep link of the two that anything in the product ever produced. The
  // Auswertung's `?projectId` and `?groupBy` are dropped rather than translated: the review matrix
  // takes a project id but means something else by it (the days, not the rollup), and a link that
  // silently answers a different question is worse than one that lands on the period's own default.
  {
    path: "/timetracking/team",
    redirect: to => ({ name: "TimeTrackingReview", hash: to.hash, query: to.query }),
  },
  {
    path: "/timetracking/analytics",
    redirect: { name: "TimeTrackingReview" },
  },
  // The Arbeitszeitkonto and the Arbeitszeitgesetz-Verstöße were pages, then tabs, then folded
  // regions — and are now neither: the account is two columns of every row of Zeiten prüfen and the
  // findings mark every cell they are about. So the account path redirects to that page and nothing
  // else, because being on it *is* arriving at what it named; the compliance path carries
  // `#violations`, so it lands on the reading that paints those findings. They were addresses for
  // long enough that links to them exist, and `/timetracking/account` was a bookmark a Lohnbüro kept.
  {
    path: "/timetracking/account",
    redirect: { name: "TimeTrackingReview" },
  },
  {
    path: "/timetracking/compliance",
    redirect: { name: "TimeTrackingReview", hash: "#violations" },
  },
]
